import React, { useState, useEffect } from "react";
import axios from "axios";
import { Box, Stack, Typography, Skeleton, LinearProgress } from "@mui/material";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";

const AdminClientSubscriptionWidget = ({ productType }) => {
  const [loader, setLoader] = useState(true);
  const [subData, setSubData] = useState(null);
  const [numberOfReportsConsumed, setNumberOfReportsConsumed] = useState(0);
  const [reRenderComponent, setReRenderComponent] = useState(false);

  useEffect(() => {
    const handleStorage = () => setReRenderComponent((prev) => !prev);
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoader(true);
      try {
        const loginDetails = JSON.parse(localStorage.getItem("loginDetails"));
        const role = loginDetails?.role;
        const orgPreference = JSON.parse(localStorage.getItem("orgPreference"));
        const clientPreference = JSON.parse(localStorage.getItem("clientPreference"));
        const clientIdPreference = JSON.parse(localStorage.getItem("clientIdPreference"));
        
        if (role !== "admin" || !orgPreference || !clientPreference || clientPreference === "none") {
           setLoader(false);
           return;
        }

        const clientsData = loginDetails.clientData || [];
        let selectedUserId = "";
        let actualClientId = "";
        for (let each of clientsData) {
          if (each.org === orgPreference && each.center === clientPreference) {
             selectedUserId = each.userId;
             actualClientId = each.clientId;
             break;
          }
        }
        
        if (!selectedUserId) {
           setLoader(false);
           return;
        }

        console.log("AdminClientSubscriptionWidget: selectedUserId =", selectedUserId, "actualClientId =", actualClientId);
        const idToken = localStorage.getItem("idTokenBilling");

        // 1. Fetch Subscription Status
        const subEndpoint = `https://rnxsohimg1.execute-api.ap-south-1.amazonaws.com/Production/subscription/status?userId=${selectedUserId}`;
        console.log("AdminClientSubscriptionWidget: fetching status from", subEndpoint);
        const statusResponse = await axios.get(subEndpoint, { headers: { "Content-Type": "application/json" } });
        console.log("AdminClientSubscriptionWidget: statusResponse", statusResponse);
        
        const subscriptionInfo = statusResponse.data.data;
        console.log("AdminClientSubscriptionWidget: subscriptionInfo", subscriptionInfo);
        setSubData(subscriptionInfo);

        // 2. Format Dates for Range API
        let countSum = 0;
        if (subscriptionInfo && subscriptionInfo.startDate && subscriptionInfo.endDate) {
          const parseDateString = (rawStr) => {
            const raw = rawStr.split(" ")[0];
            const parts = raw.split("-");
            if (parts.length === 3) {
              if (parts[0].length === 4) return new Date(`${parts[0]}-${parts[1]}-${parts[2]}T00:00:00`);
              if (parts[2].length === 4) return new Date(`${parts[2]}-${parts[1]}-${parts[0]}T00:00:00`);
            }
            return new Date(raw);
          };

          const sDate = parseDateString(subscriptionInfo.startDate);
          const eDate = new Date(); // Use today's date instead of subscription end date

          if (!Number.isNaN(sDate.getTime()) && !Number.isNaN(eDate.getTime())) {
            const fmt = (d, isEnd) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${isEnd ? "23:59:59.999" : "00:00:00.000"}`;
            
            const startParam = fmt(sDate, false);
            const endParam = fmt(eDate, true);
            
            // 3. Fetch usage counts using RANGE API
            const countUrl = `https://api.niroggyan.com/billing/${productType}/counts?query_on=counts&query_by=range&start=${startParam}&end=${endParam}&org=${encodeURIComponent(orgPreference)}&center=${encodeURIComponent(clientPreference)}&clientId=${actualClientId}`;
            console.log("AdminClientSubscriptionWidget: fetching range counts from", countUrl);
            const countResponse = await axios.get(countUrl, { headers: { authorization: idToken } });
            console.log("AdminClientSubscriptionWidget: countResponse (Range Data)", countResponse);
            
            if (countResponse.data.isSuccess && Array.isArray(countResponse.data.data)) {
              for (let item of countResponse.data.data) {
                const c = parseInt(item.count, 10);
                if (!Number.isNaN(c)) {
                  countSum += c;
                }
              }
            }
          }
        }
        setNumberOfReportsConsumed(countSum || 0);

      } catch (err) {
        console.error("AdminClientSubscriptionWidget: Error fetching admin client subscription logic", err);
      }
      setLoader(false);
    };

    fetchData();
    window.addEventListener("subscription-updated", fetchData);
    return () => window.removeEventListener("subscription-updated", fetchData);
  }, [productType, reRenderComponent]);

  const loginDetails = JSON.parse(localStorage.getItem("loginDetails"));
  const role = loginDetails?.role;
  const clientPreference = JSON.parse(localStorage.getItem("clientPreference"));
  
  if (role !== "admin" || !clientPreference || clientPreference === "none") return null;

  const usagePct = subData?.reportsSubscribed > 0
    ? Math.min(Math.round((numberOfReportsConsumed / subData.reportsSubscribed) * 100), 100)
    : 0;

  const remaining = Math.max(0, (subData?.reportsSubscribed || 0) - numberOfReportsConsumed);

  const getParsedDate = (rawStr) => {
    if (!rawStr) return null;
    const raw = rawStr.split(" ")[0];
    const parts = raw.split("-");
    if (parts.length === 3) {
      if (parts[0].length === 4) return new Date(`${parts[0]}-${parts[1]}-${parts[2]}T00:00:00`);
      if (parts[2].length === 4) return new Date(`${parts[2]}-${parts[1]}-${parts[0]}T00:00:00`);
    }
    const fallback = new Date(raw);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  };

  const parsedStartDate = getParsedDate(subData?.startDate);
  const parsedEndDate = getParsedDate(subData?.endDate);

  const remainingDays = parsedEndDate
    ? Math.max(0, Math.ceil((parsedEndDate.getTime() - new Date().getTime()) / 86400000))
    : null;

  const totalSubDays = parsedStartDate && parsedEndDate
    ? Math.max(1, Math.ceil((parsedEndDate.getTime() - parsedStartDate.getTime()) / 86400000))
    : null;

  const daysElapsed = parsedStartDate
    ? Math.max(1, Math.ceil((new Date().getTime() - parsedStartDate.getTime()) / 86400000))
    : null;

  const timePct = totalSubDays && daysElapsed !== null
    ? Math.min(Math.round((daysElapsed / totalSubDays) * 100), 100)
    : null;

  const fillAnimKey = "adminusage";

  const modifySubscriptionData = () => {
    if (!subData) return "";
    let temp = subData.subscription || subData.planName;
    if (temp === "smartreport") return "Smart Report";
    if (temp === "vizapp") return "Viz App";
    if (temp === "videoreport") return "Smart Video Report";
    return temp || "Active Plan";
  };

  const formatDateString = (rawStr) => {
    if (!rawStr) return "—";
    return rawStr.split(" ")[0].split("-").reverse().join("/");
  };
  
  const paceGap = (usagePct ?? 0) - (timePct ?? 0);
  const paceLabel = paceGap > 12 ? "Ahead of pace" : paceGap < -12 ? "Under pace" : "On track";
  const paceColor = paceGap > 12 ? "#d97706" : paceGap < -12 ? "#16a34a" : "#3567D6";
  const paceBg   = paceGap > 12 ? "#fffbeb" : paceGap < -12 ? "#f0fdf4" : "#eff6ff";
  const usageBarColor = usagePct >= 85 ? "#EF4444" : usagePct >= 60 ? "#d97706" : "#16a34a";
  const daysColor = remainingDays !== null && remainingDays < 30 ? "#EF4444" : "#3567D6";

  return (
    <>
      {/* Top Plan Period Card */}
      {!loader && (
        <Box
          sx={{
            background: "#fff",
            marginTop: "10px",
            borderRadius: "20px",
            p: { xs: "20px", md: "24px 32px" },
            border: "1px solid #EBF0FF",
            mb: 3,
            boxShadow: "0 2px 14px rgba(53,103,214,0.03)",
          }}
        >
          {/* Top Header Row */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
            <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
              <Box sx={{ background: "#F0F4FF", p: "8px", borderRadius: "8px", display: "flex" }}>
                <CalendarMonthOutlinedIcon sx={{ fontSize: 20, color: "#3567D6" }} />
              </Box>
              <Typography sx={{ fontSize: "16px", color: "#170C6B", fontWeight: 800 }}>Plan Period</Typography>
              
              <Box sx={{ background: "#EEF2FF", borderRadius: "6px", px: "10px", py: "4px", border: "1px solid #D1DEF9", ml: 0.5 }}>
                <Typography sx={{ fontSize: "11px", color: "#3567D6", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  {modifySubscriptionData()}
                </Typography>
              </Box>

              {/* Live Status Indicator */}
              {subData && (() => {
                const statusStr = (subData?.subscriptionStatus || subData?.status || "").toString().toLowerCase();
                // Assume Live if explicitly "live" or active, fallback string if other
                const isLive = statusStr === "live" || statusStr === "active" || subData?.isActive === true;
                const isWarning = statusStr === "on hold" || statusStr === "on-hold";
                
                let dotColor = "#94A3B8";
                let bgColor = "#F8FAFC";
                let borderColor = "#E2E8F0";
                let textColor = "#64748B";
                
                if (isLive) {
                  dotColor = "#22C55E"; bgColor = "#F0FDF4"; borderColor = "#BBF7D0"; textColor = "#16A34A";
                } else if (isWarning) {
                  dotColor = "#EAB308"; bgColor = "#FEFCE8"; borderColor = "#FEF08A"; textColor = "#CA8A04";
                } else if (statusStr) {
                  dotColor = "#EF4444"; bgColor = "#FEF2F2"; borderColor = "#FECACA"; textColor = "#DC2626";
                }

                // If we don't know the status, render "LIVE" conditionally if within dates
                // but usually the DB has subscriptionStatus. Let's just use what's there.
                const fallbackLabel = subData?.reportsSubscribed ? "LIVE" : "INACTIVE"; 
                const label = isLive ? "LIVE" : (statusStr ? statusStr.toUpperCase() : fallbackLabel);
                
                return (
                  <Stack direction="row" alignItems="center" gap={0.75} sx={{ background: bgColor, border: `1px solid ${borderColor}`, borderRadius: "99px", px: "8px", py: "3.5px" }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: dotColor }} />
                    <Typography sx={{ fontSize: "10.5px", color: textColor, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.5px", lineHeight: 1 }}>
                      {label}
                    </Typography>
                  </Stack>
                );
              })()}
            </Stack>
            
            <Box sx={{ background: paceBg, border: `1px solid ${paceColor}40`, borderRadius: "6px", px: "12px", py: "6px" }}>
              <Typography sx={{ fontSize: "11px", fontWeight: 700, color: paceColor, textTransform: "uppercase", letterSpacing: "0.5px" }}>{paceLabel}</Typography>
            </Box>
          </Stack>

          {/* Dates Box Only */}
          <Stack direction="row" alignItems="center" gap={5} sx={{ background: "#F8FAFF", p: "18px 28px", borderRadius: "14px", border: "1px solid #EEF2FF", width: "fit-content" }}>
            <Box>
              <Typography sx={{ fontSize: "12px", color: "#9DA3AE", fontWeight: 700, mb: 0.5, textTransform: "uppercase", letterSpacing: "0.5px" }}>Started</Typography>
              <Typography sx={{ fontSize: "16px", fontWeight: 800, color: "#170C6B" }}>{formatDateString(subData?.startDate)}</Typography>
            </Box>
            <Box sx={{ height: 32, width: "1px", background: "#D1D9E8" }} />
            <Box>
              <Typography sx={{ fontSize: "12px", color: "#9DA3AE", fontWeight: 700, mb: 0.5, textTransform: "uppercase", letterSpacing: "0.5px" }}>Ends</Typography>
              <Typography sx={{ fontSize: "16px", fontWeight: 800, color: "#170C6B" }}>{formatDateString(subData?.endDate)}</Typography>
            </Box>
          </Stack>
        </Box>
      )}

      {/* Main Admin Widget */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "16px",
          p: { xs: "16px", md: "20px 24px" },
          boxShadow: "0 2px 16px rgba(53,103,214,0.08)",
          mb: 3
        }}
      >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2.5}>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: "15px", color: "#170C6B", mb: 0.3 }}>
            Client Subscription Usage
          </Typography>
          <Typography sx={{ fontSize: "11.5px", color: "#9DA3AE" }}>
            Monthly report consumption for {JSON.parse(localStorage.getItem("orgPreference")) || "Client"}
          </Typography>
        </Box>
        <Box
          sx={{
            background: "linear-gradient(135deg, #EEF2FF 0%, #E0E8FF 100%)",
            borderRadius: "10px",
            px: 1.5,
            py: 0.75,
          }}
        >
          <Typography sx={{ fontSize: "13px", fontWeight: 800, color: "#3567D6" }}>
            {usagePct}% Used
          </Typography>
        </Box>
      </Stack>

      {loader ? (
        <Stack direction={{ xs: "column", md: "row" }} gap={2.5} alignItems={{ xs: "center", md: "flex-start" }} sx={{ width: "100%" }}>
          <Skeleton variant="rounded" width={64} height={280} sx={{ borderRadius: "8px 8px 32px 32px", flexShrink: 0 }} />
          <Stack gap={1.5} sx={{ flex: 1, width: "100%" }}>
            <Stack direction="row" gap={1.5}>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rounded" height={130} sx={{ flex: 1, borderRadius: "14px" }} />
              ))}
            </Stack>
            <Skeleton variant="rounded" height={76} sx={{ borderRadius: "14px" }} />
          </Stack>
        </Stack>
      ) : (
        <Stack direction={{ xs: "column", md: "row" }} gap={3} alignItems={{ xs: "center", md: "flex-start" }} sx={{ width: "100%" }}>
          {/* Glass Test Tube */}
          <Stack alignItems="center" gap={1.25} sx={{ flexShrink: 0 }}>
            <Box
              sx={{
                width: 64,
                height: 216,
                borderRadius: "8px 8px 32px 32px",
                border: "2.5px solid rgba(53,103,214,0.18)",
                background: "linear-gradient(to right, rgba(255,255,255,0.95) 0%, rgba(225,235,255,0.55) 50%, rgba(225,235,255,0.65) 100%)",
                position: "relative",
                overflow: "hidden",
                boxShadow: "inset 0 2px 12px rgba(53,103,214,0.07), 0 8px 28px rgba(53,103,214,0.13)",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: `${usagePct}%`,
                  background: "linear-gradient(to top, #1a3fa3 0%, #3567D6 52%, #5294FF 100%)",
                  [`@keyframes fillOnce_${fillAnimKey}`]: {
                    "0%":   { height: "0%", opacity: 0.7 },
                    "75%":  { height: `${Math.min(usagePct + 2, 100)}%`, opacity: 1 },
                    "88%":  { height: `${Math.max(usagePct - 1, 0)}%` },
                    "100%": { height: `${usagePct}%` },
                  },
                  animation: `fillOnce_${fillAnimKey} 2s cubic-bezier(0.22, 1, 0.36, 1) forwards`,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: "-15%",
                    right: "-15%",
                    height: 20,
                    background: "rgba(255,255,255,0.22)",
                    borderRadius: "50%",
                    transform: "translateY(-50%)",
                    "@keyframes wave": {
                      "0%":   { transform: "translateY(-50%) scaleX(1) translateX(0%)" },
                      "30%":  { transform: "translateY(-56%) scaleX(1.1) translateX(2%)" },
                      "60%":  { transform: "translateY(-44%) scaleX(0.95) translateX(-2%)" },
                      "100%": { transform: "translateY(-50%) scaleX(1) translateX(0%)" },
                    },
                    animation: "wave 3s ease-in-out 2s infinite",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: 10,
                    left: "18%",
                    right: "18%",
                    height: 4,
                    background: "rgba(255,255,255,0.3)",
                    borderRadius: "999px",
                    "@keyframes shimmer": {
                      "0%, 100%": { opacity: 0.3 },
                      "50%": { opacity: 0.7 },
                    },
                    animation: "shimmer 2.8s ease-in-out 2s infinite",
                  }}
                />
              </Box>
              <Box sx={{ position: "absolute", top: 12, left: 9, width: 7, height: "50%", background: "rgba(255,255,255,0.72)", borderRadius: "4px", zIndex: 3, pointerEvents: "none" }} />
              <Box sx={{ position: "absolute", top: 18, left: 20, width: 4, height: "26%", background: "rgba(255,255,255,0.42)", borderRadius: "4px", zIndex: 3, pointerEvents: "none" }} />
              {[25, 50, 75].map((tick) => (
                <Box key={tick} sx={{ position: "absolute", right: 0, bottom: `${tick}%`, width: 10, height: "1.5px", background: "rgba(53,103,214,0.22)", zIndex: 3 }} />
              ))}
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: "20px", color: "#3567D6", lineHeight: 1 }}>
              {usagePct}%
            </Typography>
            <Typography sx={{ fontSize: "14.5px", fontWeight: 'bold', color: "#8d8d8dff", mt: -0.25, letterSpacing: "0.3px" }}>
              Used
            </Typography>
          </Stack>

          {/* Bento grid */}
          <Stack gap={1.5} sx={{ flex: 1, width: { xs: "100%", md: "auto" } }}>
            <Stack direction="row" gap={1.5} sx={{ overflowX: "auto", pb: { xs: 1, md: 0 }, width: "100%" }}>
              {/* Total Limit */}
              <Box
                sx={{
                  background: "#F7F9FF",
                  borderRadius: "12px",
                  p: "11px",
                  flex: 1,
                  minWidth: { xs: 130, md: "auto" },
                  border: "1px solid #EBF0FF",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                <Box sx={{ background: "#EEF2FF", borderRadius: "8px", p: "5px", width: "fit-content", mb: 0.25 }}>
                  <BusinessCenterOutlinedIcon sx={{ fontSize: 15, color: "#8B9EC7", display: "block" }} />
                </Box>
                <Typography sx={{ fontSize: "9px", color: "#9DA3AE", textTransform: "uppercase", letterSpacing: "0.6px", fontWeight: 700 }}>
                  Total Limit
                </Typography>
                <Typography sx={{ fontSize: "18px", fontWeight: 800, color: "#4B5882", lineHeight: 1.1 }}>
                  {subData?.reportsSubscribed ? Number(subData.reportsSubscribed).toLocaleString("en-IN") : "—"}
                </Typography>
                <Box
                  sx={{
                    display: "inline-flex",
                    background: "#E8EEFF",
                    borderRadius: "5px",
                    px: 0.8,
                    py: 0.25,
                    width: "fit-content",
                    mt: 0.1,
                  }}
                >
                  <Typography sx={{ fontSize: "9px", color: "#3567D6", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 90 }}>
                    {modifySubscriptionData()}
                  </Typography>
                </Box>
              </Box>

              {/* Reports Used */}
              <Box
                sx={{
                  background: "linear-gradient(145deg, #EEF3FF 0%, #E2ECFF 100%)",
                  borderRadius: "12px",
                  p: "11px",
                  flex: 1,
                  minWidth: { xs: 130, md: "auto" },
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                <Box sx={{ background: "rgba(53,103,214,0.12)", borderRadius: "8px", p: "5px", width: "fit-content", mb: 0.25 }}>
                  <AssessmentOutlinedIcon sx={{ fontSize: 15, color: "#3567D6", display: "block" }} />
                </Box>
                <Typography sx={{ fontSize: "9px", color: "#8894B4", textTransform: "uppercase", letterSpacing: "0.6px", fontWeight: 700 }}>
                  Reports Used
                </Typography>
                {subData?.startDate && (
                  <Typography sx={{ fontSize: "10px", color: "#5567A0", fontWeight: 600, whiteSpace: "nowrap" }}>
                    {formatDateString(subData.startDate)} – {new Date().toLocaleDateString("en-GB").replace(/\//g, "/")}
                  </Typography>
                )}
                <Typography sx={{ fontSize: "18px", fontWeight: 800, color: "#3567D6", lineHeight: 1.1 }}>
                  {numberOfReportsConsumed.toLocaleString("en-IN")}
                </Typography>
              </Box>

              {/* Remaining */}
              <Box
                sx={{
                  background: "linear-gradient(145deg, #EDFAF4 0%, #D9F5E7 100%)",
                  borderRadius: "12px",
                  p: "11px",
                  flex: 1,
                  minWidth: { xs: 130, md: "auto" },
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                <Box sx={{ background: "rgba(34,197,94,0.14)", borderRadius: "8px", p: "5px", width: "fit-content", mb: 0.25 }}>
                  <VerifiedOutlinedIcon sx={{ fontSize: 15, color: "#22C55E", display: "block" }} />
                </Box>
                <Typography sx={{ fontSize: "9px", color: "#6AAF8A", textTransform: "uppercase", letterSpacing: "0.6px", fontWeight: 700 }}>
                  Remaining
                </Typography>
                <Typography sx={{ fontSize: "18px", fontWeight: 800, color: "#15803D", lineHeight: 1.1 }}>
                  {remaining.toLocaleString("en-IN")}
                </Typography>
              </Box>
            </Stack>

            {/* Plan Period */}
            <Box sx={{ width: { xs: "100%", md: "78%" }, mx: "auto", display: { xs: "none", md: "block" }, mt: 1.5, overflowX: "auto", pb: { xs: 1, md: 0 } }}>
              <Box sx={{ background: "linear-gradient(135deg, #F8FAFF 0%, #EEF3FF 100%)", borderRadius: "14px", p: "14px 18px", border: "1px solid #E8EEF8", minWidth: { xs: 320, md: "auto" } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Stack direction="row" alignItems="center" gap={0.6}>
                    <CalendarMonthOutlinedIcon sx={{ fontSize: 12, color: "#3567D6" }} />
                    <Typography sx={{ fontSize: "9px", color: "#9DA3AE", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 700 }}>Plan Period</Typography>
                  </Stack>
                  <Box sx={{ background: paceBg, border: `1px solid ${paceColor}30`, borderRadius: "6px", px: "7px", py: "2px" }}>
                    <Typography sx={{ fontSize: "9px", fontWeight: 700, color: paceColor }}>{paceLabel}</Typography>
                  </Box>
                </Stack>
                <Stack direction="row" alignItems="flex-start" gap={2}>
                  <Box sx={{ flexShrink: 0, minWidth: 70 }}>
                    <Typography sx={{ fontSize: "10.5px", fontWeight: 700, color: "#170C6B" }}>{formatDateString(subData?.startDate)}</Typography>
                    <Typography sx={{ fontSize: "9.5px", color: "#C4CBD8", my: 0.2 }}>to</Typography>
                    <Typography sx={{ fontSize: "10.5px", fontWeight: 700, color: "#170C6B" }}>{formatDateString(subData?.endDate)}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" alignItems="center" gap={1} mb={1}>
                      <Typography sx={{ fontSize: "9px", color: "#9DA3AE", fontWeight: 600, minWidth: 66, textTransform: "uppercase", letterSpacing: "0.4px" }}>Time</Typography>
                      <Box sx={{ flex: 1, height: 9, borderRadius: "999px", background: "rgba(53,103,214,0.1)", position: "relative" }}>
                        <Box sx={{ position: "absolute", top: 0, left: 0, height: "100%", width: `${timePct ?? 0}%`, background: "linear-gradient(90deg, #3567D6 0%, #5294FF 100%)", borderRadius: "999px", [`@keyframes tF_${fillAnimKey}`]: { "0%": { width: "0%" }, "100%": { width: `${timePct ?? 0}%` } }, animation: `tF_${fillAnimKey} 1.8s ease-out forwards` }} />
                        <Box sx={{ position: "absolute", top: "50%", left: `${timePct ?? 0}%`, transform: "translate(-50%,-50%)", zIndex: 2 }}>
                          <Box sx={{ position: "absolute", width: 20, height: 20, borderRadius: "50%", background: "rgba(53,103,214,0.18)", top: "50%", left: "50%", transform: "translate(-50%,-50%)", "@keyframes pulse": { "0%": { transform: "translate(-50%,-50%) scale(0.8)", opacity: 1 }, "100%": { transform: "translate(-50%,-50%) scale(2)", opacity: 0 } }, animation: "pulse 2.2s ease-out 1.8s infinite" }} />
                          <Box sx={{ width: 11, height: 11, borderRadius: "50%", background: "#3567D6", border: "2.5px solid #fff", boxShadow: "0 2px 8px rgba(53,103,214,0.4)", position: "relative" }} />
                        </Box>
                      </Box>
                      <Typography sx={{ fontSize: "9px", color: "#3567D6", fontWeight: 700, minWidth: 32, textAlign: "right" }}>{timePct ?? 0}%</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography sx={{ fontSize: "9px", color: "#9DA3AE", fontWeight: 600, minWidth: 66, textTransform: "uppercase", letterSpacing: "0.4px" }}>Reports</Typography>
                      <Box sx={{ flex: 1, height: 9, borderRadius: "999px", background: "rgba(0,0,0,0.06)", position: "relative" }}>
                        <Box sx={{ position: "absolute", top: 0, left: 0, height: "100%", width: "0%", background: usagePct >= 85 ? "linear-gradient(90deg, #EF4444 0%, #F87171 100%)" : usagePct >= 60 ? "linear-gradient(90deg, #d97706 0%, #fbbf24 100%)" : "linear-gradient(90deg, #16a34a 0%, #4ade80 100%)", borderRadius: "999px", [`@keyframes uF_${fillAnimKey}`]: { "0%": { width: "0%" }, "100%": { width: `${usagePct ?? 0}%` } }, animation: `uF_${fillAnimKey} 2s ease-out 0.3s forwards` }} />
                      </Box>
                      <Typography sx={{ fontSize: "9px", color: usageBarColor, fontWeight: 700, minWidth: 32, textAlign: "right" }}>{usagePct ?? 0}%</Typography>
                    </Stack>
                  </Box>
                  <Box sx={{ flexShrink: 0, textAlign: "center", minWidth: 68, background: "#fff", borderRadius: "12px", p: "8px 12px", boxShadow: "0 2px 10px rgba(53,103,214,0.09)" }}>
                    <Typography sx={{ fontSize: "22px", fontWeight: 800, color: daysColor, lineHeight: 1 }}>{remainingDays !== null ? remainingDays : "—"}</Typography>
                    <Typography sx={{ fontSize: "9px", color: "#9DA3AE", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", mt: 0.35 }}>days left</Typography>
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Stack>
        </Stack>
      )}
    </Box>
    </>
  );
};

export default AdminClientSubscriptionWidget;
